/**
 * Source Card Component
 * Displays a single source reference with title, content type, and ordinal number
 */

import '../../../../styles/oc-utilities.css';

import React, { useCallback, useMemo } from 'react';

import { MessageReference } from '../../../Message/types/messages';
import ContentTypeBadge, { ContentType } from './ContentTypeBadge';
import {
  BadgeContainer,
  CardContainer,
  ContentColumn,
  OrdinalColumn,
  Title,
} from './SourceCard.styled';

export type SourceCardProps = {
  source: MessageReference;
  ordinal: number;
  className?: string;
};

/**
 * Map AI item types to ContentType enum
 * Source of truth: online-community/Assets/projects/ReactRoot/src/AI/sources/SearchResultSource.tsx
 */
const AI_ITEM_TYPE_MAP: Record<string, ContentType> = {
  announcement: 'Announcement',
  answer: 'Answer',
  blog: 'Blog',
  event: 'CalendarEvent',
  calendarevent: 'CalendarEvent',
  community: 'Community',
  egroupmessage: 'MessagePost',
  eventoption: 'CalendarEvent',
  glossary: 'Glossary',
  ideation: 'Ideation',
  library: 'LibraryEntry',
  libraryentry: 'LibraryEntry',
  libraryentryfile: 'LibraryEntry',
  messagethread: 'MessageThread',
  navigation: 'Page',
  question: 'Question',
  volunteeropportunity: 'VolunteerOpportunity',
  automatedresponseref: 'AutomatedResponseRef',
};

/**
 * Line clamp component - limits text to N lines
 */
const LineClamp: React.FC<{ lines: number; children: React.ReactNode }> = ({ lines, children }) => (
  <div
    style={{
      display: '-webkit-box',
      WebkitLineClamp: lines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    }}
  >
    {children}
  </div>
);

/**
 * SourceCard Component
 * Displays a source reference card with ordinal number, title, and content type badge
 */
const SourceCard: React.FC<SourceCardProps> = ({ source, ordinal, className = '' }) => {
  const isAutomatedResponse = source.itemType === 'automatedresponseref';
  const contentType = AI_ITEM_TYPE_MAP[source.itemType.toLowerCase()];
  const isClickable = !isAutomatedResponse && !!source.url;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isClickable) {
        e.preventDefault();
      }
    },
    [isClickable]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
        window.open(source.url, '_blank', 'noopener,noreferrer');
      }
    },
    [isClickable, source.url]
  );

  const cardContent = useMemo(
    () => (
      <CardContainer $isClickable={isClickable}>
        {/* Ordinal number column */}
        <OrdinalColumn aria-hidden="true">{ordinal}</OrdinalColumn>

        {/* Content column */}
        <ContentColumn>
          {/* Title */}
          <Title $isAutomated={isAutomatedResponse}>
            <LineClamp lines={3}>{source.title}</LineClamp>
          </Title>

          {/* Content type badge */}
          {contentType && (
            <BadgeContainer>
              <ContentTypeBadge contentType={contentType} />
            </BadgeContainer>
          )}
        </ContentColumn>
      </CardContainer>
    ),
    [ordinal, source.title, contentType, isAutomatedResponse, isClickable]
  );

  if (isAutomatedResponse) {
    return (
      <div className={className} data-qa-id="source-card">
        {cardContent}
      </div>
    );
  }

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      data-qa-id="source-card"
      aria-label={`View source: ${source.title}`}
    >
      {cardContent}
    </a>
  );
};

export default React.memo(SourceCard);
